import { stockApi } from '$api'
import { IItemSupplierVariant, ItemSupplierVariant } from '$models'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'

enum QueryKeys {
  GetVariantViaCodeItemIdAndSupplierId = 'getVariantViaCodeItemIdAndSupplierID',
}

/**
 * Updates the entire item supplier variant
 * @param itemSupplierVariant the item supplier variant with updates
 * @returns the updated item supplier variant
 */
export const updateItemSupplierVariant = async (
  itemSupplierVariant: IItemSupplierVariant
): Promise<ItemSupplierVariant> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) {
    throw new Error(
      "The user doesn't have permission to update a item supplier variant."
    )
  }

  return await stockApi.updateItemSupplierVariant(
    itemSupplierVariant.id!,
    itemSupplierVariant as ItemSupplierVariant
  )
}

/**
 * Updates the stock item, brand, and/or unit of a item supplier variant
 * @param itemSupplierVariantId the ID of the variant to update
 * @param stockItemId the ID of the stock item
 * @param brandId the ID of the brand
 * @param unitId the ID of the unit
 * @returns the updated stock item variant
 */
export const patchItemSupplierVariant = async (
  itemSupplierVariantId: string,
  stockItemId?: string,
  brandId?: string | null,
  unitId?: string
): Promise<IItemSupplierVariant> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) {
    throw new Error(
      "The user doesn't have permission to update a item supplier variant."
    )
  }

  return await stockApi.partialUpdateItemSupplierVariant(
    itemSupplierVariantId,
    {
      ...(stockItemId && { stockItemId: stockItemId.toString() }),
      ...(brandId !== undefined &&
        brandId !== null && {
          brandId: brandId.toString(),
        }),
      ...(unitId && { unitId: unitId.toString() }),
    }
  )
}

/**
 * Retrieves an item supplier variant via its code, item ID, and supplier ID
 * @param code the code of the variant
 * @param itemId the item ID the variant belongs to
 * @param supplierId the supplier ID the variant comes under
 * @returns the related item supplier variant
 */
export const getItemSupplierVariantViaCodeItemIdAndSupplierId = async (
  code: string,
  itemId: string,
  supplierId: string
): Promise<IItemSupplierVariant> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) {
    throw new Error(
      "The user doesn't have permission to get a item supplier variant."
    )
  }

  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.GetVariantViaCodeItemIdAndSupplierId,
      code,
      itemId,
      supplierId,
    ],
    queryFn: async () => {
      return await stockApi.getItemSupplierVariantViaCodeItemIdAndSupplierId(
        code,
        itemId,
        supplierId
      )
    },
  })
}

export const removeItemSupplierVariant = async (
  itemSupplierId: string
): Promise<void> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) {
    throw new Error(
      "The user doesn't have permission to remove a item supplier variant."
    )
  }

  await stockApi.removeItemSupplierVariant(itemSupplierId)
}
